import React from 'react';
import { BlobItem } from '../types';

type SortField = 'name' | 'lastModified' | 'size';
type SortDirection = 'asc' | 'desc';

interface FileTableProps {
  displayItems: BlobItem[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onNavigate: (path: string, isDirectory: boolean) => void;
  formatDate: (date: Date) => string;
  formatFileSize: (bytes: number) => string;
}

const FileTable: React.FC<FileTableProps> = ({
  displayItems,
  sortField,
  sortDirection,
  onSort,
  onNavigate,
  formatDate,
  formatFileSize
}) => {
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#F96423' }}>
        {sortDirection === 'asc' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
        )}
      </svg>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="bg-white shadow-sm border-0 overflow-hidden flex flex-col flex-1">
        {/* Fixed header */}
        <div className="bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <table className="min-w-full table-fixed">
            <colgroup>
              <col className="w-2/5" />
              <col className="w-1/4" />
              <col className="w-1/6" />
              <col className="w-1/6" />
            </colgroup>
            <thead>
              <tr>
                <th className="px-8 py-4 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: '#4B5563' }}>
                  <button
                    onClick={() => onSort('name')}
                    className="flex items-center space-x-2 hover:opacity-80 transition-colors"
                  >
                    <span>Name</span>
                    {renderSortIcon('name')}
                  </button>
                </th>
                <th className="px-8 py-4 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: '#4B5563' }}>
                  <button
                    onClick={() => onSort('lastModified')}
                    className="flex items-center space-x-2 hover:opacity-80 transition-colors"
                  >
                    <span>Last Modified</span>
                    {renderSortIcon('lastModified')}
                  </button>
                </th>
                <th className="px-8 py-4 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: '#4B5563' }}>
                  <button
                    onClick={() => onSort('size')}
                    className="flex items-center space-x-2 hover:opacity-80 transition-colors"
                  >
                    <span>Size</span>
                    {renderSortIcon('size')}
                  </button>
                </th>
                <th className="px-8 py-4 text-left text-sm font-semibold tracking-wider" style={{ color: '#4B5563' }}>
                  Actions
                </th>
              </tr>
            </thead>
          </table>
        </div>
        
        {/* Scrollable body */}
        <div id="blob-browser-content" className="flex-1 overflow-y-auto min-h-0">
          <table className="min-w-full table-fixed">
            <colgroup>
              <col className="w-2/5" />
              <col className="w-1/4" />
              <col className="w-1/6" />
              <col className="w-1/6" />
            </colgroup>
            <tbody className="divide-y divide-gray-100">
              {displayItems.map((item, index) => (
                <tr key={index} className="hover:bg-orange-50/50 transition-all duration-200 group">
                  <td className="px-8 py-3">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-4 transition-all duration-200 ${
                        item.isDirectory 
                          ? 'bg-orange-100 group-hover:bg-orange-200' 
                          : 'bg-gray-100 group-hover:bg-gray-200'
                      }`} style={{ 
                        color: item.isDirectory ? '#F96423' : '#4B5563' 
                      }}>
                        {item.isDirectory ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </div>
                      <button
                        onClick={() => onNavigate(item.name, item.isDirectory || false)}
                        className="text-left transition-colors"
                        style={{ color: '#374151' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#293A41'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
                      >
                        <div className="text-sm font-medium transition-colors">
                          {item.name}
                        </div>
                      </button>
                    </div>
                  </td>
                  <td className="px-8 py-3" style={{ color: '#374151', fontSize: '14px' }}>
                    {item.lastModified ? (
                      <span className="font-medium">{formatDate(item.lastModified)}</span>
                    ) : (
                      <span style={{ color: '#6B7280' }}>—</span>
                    )}
                  </td>
                  <td className="px-8 py-3" style={{ color: '#374151', fontSize: '14px' }}>
                    {item.contentLength !== undefined ? (
                      <span className="font-medium">{formatFileSize(item.contentLength)}</span>
                    ) : (
                      <span style={{ color: '#6B7280' }}>—</span>
                    )}
                  </td>
                  <td className="px-8 py-3">
                    {!item.isDirectory && item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                        style={{ backgroundColor: 'rgba(249, 100, 35, 0.8)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(229, 90, 31, 0.8)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 100, 35, 0.8)'}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FileTable;
